/**
 * The Clip Looper - Main Application
 *
 * Browser-based video looping tool with crossfades.
 * All processing happens client-side via ffmpeg.wasm.
 */

import { createStore, ActionTypes } from './state.js';
import {
  loadFFmpeg,
  checkXfadeAvailability,
  checkAcrossfadeAvailability,
  probeVideo,
  getFFmpeg
} from './ffmpeg/loader.js';
import { processVideo, cleanupPreviousOutput, revokeBlobUrl } from './ffmpeg/processor.js';
import { validateFile, validateDuration, canProcess } from './utils/validation.js';
import { calculateOutputDuration } from './utils/duration.js';
import { el, clearElement, show, hide, setVisible, setText } from './ui/dom.js';
import {
  createLoadingIndicator,
  createUploadZone,
  createFileInfo,
  updateFileInfo,
  createConfigControls,
  updateDurationEstimate,
  createProcessButton,
  updateProcessButton,
  createProgressBar,
  updateProgress,
  updateProcessingEstimate,
  createVideoPreview,
  updatePreview,
  createDownloadSection,
  updateDownloadSection,
  createSettingsChangedIndicator,
  createErrorDisplay,
  showError,
  hideError,
  createUpgradePrompt,
  createMobileWarning,
  createValidationError,
  showValidationError,
  hideValidationError
} from './ui/components.js';

// Module-level state (scoped per mountTool call)
let store = null;
let acrossfadeAvailable = true;

/**
 * Build the tool UI into the provided container element
 */
function buildToolUI(container) {
  clearElement(container);

  const appContainer = el.div({ className: 'app-container' }, [
    // Mobile warning (hidden by default, shown on small screens)
    createMobileWarning(),

    // Loading indicator
    createLoadingIndicator(),

    // Main content (hidden until ffmpeg loaded)
    el.div({ className: 'main-content hidden', id: 'mainContent' }, [
      // Upload section
      el.div({ className: 'section' }, [
        createUploadZone({
          onFileSelect: handleFileSelect
        }),
        createFileInfo(),
        createValidationError()
      ]),

      // Config section
      el.div({ className: 'section' }, [
        createConfigControls({
          onRepeatChange: handleRepeatChange,
          onCrossfadeChange: handleCrossfadeChange
        })
      ]),

      // Process button
      el.div({ className: 'section' }, [
        createProcessButton({
          onProcess: handleProcess
        }),
        createProgressBar(),
        createSettingsChangedIndicator()
      ]),

      // Error display
      createErrorDisplay({
        onRetry: handleRetry
      }),

      // Preview section
      el.div({ className: 'section' }, [
        createVideoPreview()
      ]),

      // Download section
      el.div({ className: 'section' }, [
        createDownloadSection(),
        createUpgradePrompt()
      ])
    ])
  ]);

  container.appendChild(appContainer);

  // Check for mobile and show warning
  if (window.innerWidth < 768) {
    show(document.getElementById('mobileWarning'));
  }
}

/**
 * Handle file selection
 */
async function handleFileSelect(file) {
  hideError();
  hideValidationError();

  // Preliminary validation
  const validationResult = validateFile(file);
  if (!validationResult.valid) {
    showValidationError(validationResult.error);
    return;
  }

  // Update state - file selected, begin probing
  store.dispatch({ type: ActionTypes.FILE_SELECTED, payload: { file } });

  // Update UI to show analyzing state
  updateProcessButton({ disabled: true, text: 'Analyzing...', loading: true });

  try {
    const ffmpeg = getFFmpeg();
    const metadata = await probeVideo(ffmpeg, file);

    // Validate duration
    const state = store.getState();
    const durationResult = validateDuration(metadata.duration, state.config.crossfadeDuration);

    if (!durationResult.valid) {
      store.dispatch({
        type: ActionTypes.FILE_ERROR,
        payload: { message: durationResult.error }
      });
      showValidationError(durationResult.error);
      updateProcessButton({ disabled: true, text: 'Process Video', loading: false });
      return;
    }

    // File probed successfully
    window.gtag?.('event', 'tool_file_uploaded', {
      file_type: String(file.type),
      file_size_mb: String(Math.round(file.size / 1024 / 1024))
    });

    store.dispatch({
      type: ActionTypes.FILE_PROBED,
      payload: {
        name: file.name,
        size: file.size,
        duration: metadata.duration,
        width: metadata.width,
        height: metadata.height,
        hasAudio: metadata.hasAudio,
        fileObject: file
      }
    });

    // Show warning if present
    if (durationResult.warning) {
      updateFileInfo(store.getState().file, durationResult.warning);
    }

  } catch (error) {
    store.dispatch({
      type: ActionTypes.FILE_ERROR,
      payload: { message: error.message }
    });
    showValidationError('Could not analyze video file. Please try a different file.');
    updateProcessButton({ disabled: true, text: 'Process Video', loading: false });
  }
}

/**
 * Handle repeat count change
 */
function handleRepeatChange(value) {
  store.dispatch({
    type: ActionTypes.CONFIG_CHANGED,
    payload: { repeatCount: value }
  });
}

/**
 * Handle crossfade duration change
 */
function handleCrossfadeChange(value) {
  store.dispatch({
    type: ActionTypes.CONFIG_CHANGED,
    payload: { crossfadeDuration: value }
  });
}

/**
 * Handle process button click
 */
async function handleProcess() {
  const state = store.getState();

  // Validate
  const canProcessResult = canProcess(state);
  if (!canProcessResult.valid) {
    showValidationError(canProcessResult.error);
    return;
  }

  hideError();
  hideValidationError();

  // Clean up previous output
  const ffmpeg = getFFmpeg();
  if (state.output) {
    await cleanupPreviousOutput(ffmpeg, state.output);
  }

  // Start processing
  window.gtag?.('event', 'tool_processing_started', {
    repeat_count: String(state.config.repeatCount),
    crossfade_duration: String(state.config.crossfadeDuration)
  });

  const processingStartTime = performance.now();

  store.dispatch({ type: ActionTypes.PROCESSING_START });

  try {
    const result = await processVideo(ffmpeg, {
      file: state.file.fileObject,
      duration: state.file.duration,
      hasAudio: state.file.hasAudio,
      repeatCount: state.config.repeatCount,
      crossfadeDuration: state.config.crossfadeDuration,
      useAcrossfade: acrossfadeAvailable,
      onProgress: (progress) => {
        store.dispatch({
          type: ActionTypes.PROCESSING_PROGRESS,
          payload: { progress }
        });
      }
    });

    const durationSeconds = Math.round((performance.now() - processingStartTime) / 1000);

    window.gtag?.('event', 'tool_processing_complete', {
      repeat_count: String(state.config.repeatCount),
      crossfade_duration: String(state.config.crossfadeDuration),
      output_duration: String(Math.round(result.duration)),
      output_size_mb: String(Math.round(result.fileSize / 1024 / 1024))
    });

    window.gtag?.('event', 'tool_processing_duration', {
      duration_seconds: String(durationSeconds),
      repeat_count: String(state.config.repeatCount),
      outcome: 'success'
    });

    store.dispatch({
      type: ActionTypes.PROCESSING_COMPLETE,
      payload: result
    });

  } catch (error) {
    const durationSeconds = Math.round((performance.now() - processingStartTime) / 1000);

    window.gtag?.('event', 'tool_processing_error', {
      error_type: String(error.name || 'UnknownError'),
      error_message: String(error.message).substring(0, 100),
      repeat_count: String(state.config.repeatCount)
    });

    window.gtag?.('event', 'tool_processing_duration', {
      duration_seconds: String(durationSeconds),
      repeat_count: String(state.config.repeatCount),
      outcome: 'error'
    });

    store.dispatch({
      type: ActionTypes.PROCESSING_ERROR,
      payload: { message: error.message }
    });
  }
}

/**
 * Handle retry button click
 */
function handleRetry() {
  hideError();
  const state = store.getState();

  // If we have a file, try processing again
  if (state.file) {
    handleProcess();
  }
}

/**
 * Render the UI based on state
 */
function render(state, prevState, action) {
  // Update file info
  if (state.file !== prevState?.file) {
    updateFileInfo(state.file);

    // Update upload zone style
    const uploadZone = document.getElementById('uploadZone');
    if (uploadZone) {
      uploadZone.classList.toggle('has-file', !!state.file);
    }
  }

  // Update duration estimate
  if (
    state.file?.duration !== prevState?.file?.duration ||
    state.config.repeatCount !== prevState?.config?.repeatCount ||
    state.config.crossfadeDuration !== prevState?.config?.crossfadeDuration
  ) {
    updateDurationEstimate(
      state.file?.duration,
      state.config.repeatCount,
      state.config.crossfadeDuration
    );

    // Validate crossfade vs duration
    if (state.file?.duration) {
      const durationResult = validateDuration(state.file.duration, state.config.crossfadeDuration);
      if (!durationResult.valid) {
        showValidationError(durationResult.error);
      } else {
        hideValidationError();
      }
    }
  }

  // Update process button
  const canProcessResult = canProcess(state);
  const isProcessing = state.processingStatus === 'processing';

  let buttonText = 'Process Video';
  if (state.ffmpegStatus === 'loading') {
    buttonText = 'Waiting for engine...';
  } else if (isProcessing) {
    buttonText = 'Processing...';
  } else if (state.processingStatus === 'complete' && state.settingsChanged) {
    buttonText = 'Re-process Video';
  }

  updateProcessButton({
    disabled: !canProcessResult.valid || isProcessing,
    text: buttonText,
    loading: isProcessing
  });

  // Update progress
  if (state.processingStatus === 'processing') {
    show(document.getElementById('progressContainer'));
    updateProgress(state.processingProgress);

    // Show time estimate when processing first starts
    if (prevState?.processingStatus !== 'processing' && state.file?.duration) {
      const outputDuration = calculateOutputDuration(
        state.file.duration,
        state.config.repeatCount,
        state.config.crossfadeDuration
      );
      updateProcessingEstimate(outputDuration);
    }
  } else {
    hide(document.getElementById('progressContainer'));
  }

  // Update settings changed indicator
  setVisible(document.getElementById('settingsChanged'), state.settingsChanged);

  // Update preview and download
  if (state.output !== prevState?.output) {
    updatePreview(state.output?.blobUrl);
    updateDownloadSection(state.output);

    // Show upgrade prompt after successful processing
    setVisible(document.getElementById('upgradePrompt'), state.processingStatus === 'complete');
  }

  // Show/hide error
  if (state.processingStatus === 'error' && state.processingError) {
    showError(state.processingError, true);
  } else if (state.processingStatus !== 'error') {
    hideError();
  }
}

/**
 * Mount the clip looper tool into a container element.
 * Returns an object with a destroy() method for cleanup.
 *
 * @param {HTMLElement} containerElement - DOM element to render the tool into
 * @returns {{ destroy: () => void }}
 */
function mountTool(containerElement) {
  // Build the tool UI
  buildToolUI(containerElement);

  // Create store with render callback
  store = createStore(render);

  // Set up beforeunload warning
  const beforeUnloadHandler = (e) => {
    const state = store.getState();
    if (state.processingStatus === 'processing') {
      e.preventDefault();
      e.returnValue = 'Video processing in progress. Are you sure you want to leave?';
      return e.returnValue;
    }
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);

  // Load FFmpeg
  setText(document.getElementById('loadingText'), 'Loading video engine (~31 MB)...');

  (async () => {
    try {
      const ffmpeg = await loadFFmpeg();

      // Check filter availability
      setText(document.getElementById('loadingText'), 'Checking filter availability...');

      const xfadeAvailable = await checkXfadeAvailability(ffmpeg);
      if (!xfadeAvailable) {
        throw new Error('Video crossfade filter (xfade) not available. Please try a different browser.');
      }

      acrossfadeAvailable = await checkAcrossfadeAvailability(ffmpeg);
      if (!acrossfadeAvailable) {
        console.warn('acrossfade not available, using afade fallback');
      }

      // FFmpeg loaded successfully
      window.gtag?.('event', 'tool_engine_loaded');

      store.dispatch({ type: ActionTypes.FFMPEG_LOADED });

      // Hide loading, show main content
      hide(document.getElementById('loadingIndicator'));
      show(document.getElementById('mainContent'));

    } catch (error) {
      store.dispatch({
        type: ActionTypes.FFMPEG_ERROR,
        payload: { message: error.message }
      });

      // Show error in loading indicator
      const loadingIndicator = document.getElementById('loadingIndicator');
      const loadingText = document.getElementById('loadingText');
      const spinner = loadingIndicator?.querySelector('.loading-spinner');

      if (spinner) hide(spinner);
      if (loadingText) {
        loadingText.style.color = '#f87171';
        setText(loadingText, `Failed to load: ${error.message}`);
      }
    }
  })();

  return {
    destroy() {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      revokeBlobUrl();
      containerElement.innerHTML = '';
    }
  };
}

export { mountTool };
